/**
 * Access Review Page — Chat-First AI Review Assistant (MVP-2)
 *
 * Conversational interface where managers review user access through dialogue.
 * The chat IS the primary interface — scope selection, user exploration, and
 * decisions all happen through natural language conversation.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import ReactMarkdown from 'react-markdown';
import {
  Shield, CheckCircle2, Flag, Ban,
  BarChart3, RefreshCw, Clock, MessageSquare,
} from 'lucide-react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ShieldIcon from '@mui/icons-material/Shield';

import graphragService from '../graphrag/services/graphragService';
import UserDetailPopup from '../graphrag/components/UserDetailPopup';

/* ─── Helpers ─── */

let msgCounter = 0;
const nextMsgId = () => `msg_${++msgCounter}`;

const TRUST_COLORS = {
  CRITICAL: '#d27373',
  DEGRADED: '#d4925e',
  FAIR: '#eab308',
  GOOD: '#5da87a',
  EXCELLENT: '#10b981',
};

const getTrustColor = (score) => {
  if (score == null) return '#6b7280';
  if (score < 30) return TRUST_COLORS.CRITICAL;
  if (score < 50) return TRUST_COLORS.DEGRADED;
  if (score < 70) return TRUST_COLORS.FAIR;
  if (score < 85) return TRUST_COLORS.GOOD;
  return TRUST_COLORS.EXCELLENT;
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
};

/* ─── Intent parsing (client-side, no LLM) ─── */

const parseScopeIntent = (text) => {
  const lower = text.toLowerCase();
  if (lower.includes('dormant') || lower.includes('inactive'))
    return { scope: 'risk_type', scopeValue: 'dormant' };
  if (lower.includes('privilege') || lower.includes('creep'))
    return { scope: 'risk_type', scopeValue: 'privilege_creep' };
  if (lower.includes('excessive'))
    return { scope: 'risk_type', scopeValue: 'excessive_access' };
  return { scope: 'all', scopeValue: null };
};

const findUserInText = (text, users) => {
  if (!users || !users.length) return null;
  const lower = text.toLowerCase();
  const byEmail = users.find((u) => u.email && lower.includes(u.email.toLowerCase()));
  if (byEmail) return byEmail;
  const byName = users.find(
    (u) => u.display_name && lower.includes(u.display_name.toLowerCase()),
  );
  return byName || null;
};

const parseDecisionIntent = (text, users) => {
  const lower = text.toLowerCase().trim();
  const decisions = ['approve', 'flag', 'revoke'];
  for (const decision of decisions) {
    if (lower.startsWith(decision) || lower.includes(`${decision} `)) {
      const matched = findUserInText(lower, users);
      if (matched) {
        return { decision, userEmail: matched.email, userName: matched.display_name };
      }
    }
  }
  return null;
};

const parseUserDetailIntent = (text, users) => {
  const lower = text.toLowerCase();
  const patterns = ['tell me about', 'show me', 'details for', 'who is', 'what about', 'analyze'];
  for (const p of patterns) {
    if (lower.includes(p)) {
      return findUserInText(lower, users);
    }
  }
  return null;
};

const buildReviewSummary = (data) => {
  const risk = data.risk_summary || {};
  const scopeLabel =
    data.scope?.type === 'risk_type' ? data.scope.value?.replace('_', ' ') : 'all';
  let md = `Found **${data.total_users || data.users?.length || 0} ${scopeLabel} users** to review.\n\n`;

  const items = [];
  if (risk.critical > 0) items.push(`- **${risk.critical} CRITICAL** risk (trust score < 30)`);
  if (risk.high > 0) items.push(`- **${risk.high} HIGH** risk (trust score 30-50)`);
  if (risk.medium > 0) items.push(`- **${risk.medium} MEDIUM** risk (trust score 50-70)`);
  if (risk.low > 0) items.push(`- **${risk.low} LOW** risk (trust score > 70)`);
  if (risk.dormant > 0) items.push(`- **${risk.dormant}** dormant accounts`);
  if (risk.privilege_creep > 0) items.push(`- **${risk.privilege_creep}** with privilege creep`);
  if (risk.excessive_access > 0) items.push(`- **${risk.excessive_access}** with excessive access`);

  if (items.length > 0) md += items.join('\n') + '\n\n';
  md += 'Who should we look at first?';
  return md;
};

/* ─── Structured Data Components ─── */

const UserMiniCard = ({ user, onDoubleClick }) => {
  const trustColor = getTrustColor(user.trust_score);
  const clickable = !!user.email && !!onDoubleClick;

  const flagColor = (flag) => {
    if (flag.startsWith('DORMANT')) return '#6366f1';
    if (flag.startsWith('PRIVILEGE')) return '#d4925e';
    if (flag.startsWith('EXCESSIVE')) return '#d27373';
    return '#6b7280';
  };

  return (
    <Box
      onDoubleClick={clickable ? () => onDoubleClick(user) : undefined}
      title={clickable ? 'Double-click for details' : undefined}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 1.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        cursor: clickable ? 'pointer' : 'default',
        '&:hover': clickable ? { bgcolor: 'action.hover' } : {},
      }}
    >
      {/* Initials avatar */}
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {getInitials(user.display_name)}
      </Box>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {user.display_name}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap display="block">
          {user.department}
          {user.job_title ? ` · ${user.job_title}` : ''}
        </Typography>
        {user.risk_flags && user.risk_flags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {user.risk_flags.map((flag, i) => (
              <Chip
                key={i}
                label={flag}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  bgcolor: flagColor(flag),
                  color: '#fff',
                }}
              />
            ))}
          </Box>
        )}
        {user.apps_detail && user.apps_detail.length > 0 && (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {user.apps_detail.join(', ')}
          </Typography>
        )}
      </Box>

      {/* Trust score */}
      <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
        <Box
          sx={{
            border: `2px solid ${trustColor}`,
            borderRadius: 1,
            px: 0.75,
            py: 0.25,
            minWidth: 40,
          }}
        >
          <Typography variant="caption" fontWeight={700} display="block">
            {user.trust_score ?? '—'}
          </Typography>
          <Typography variant="caption" sx={{ color: trustColor, fontSize: '0.6rem' }}>
            {user.trust_level || ''}
          </Typography>
        </Box>
        {(user.groups != null || user.apps != null) && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, fontSize: '0.65rem' }}>
            {user.groups != null ? `${user.groups}g` : ''}
            {user.groups != null && user.apps != null ? ' · ' : ''}
            {user.apps != null ? `${user.apps}a` : ''}
          </Typography>
        )}
        {user.user_type && user.user_type !== 'Member' && (
          <Chip
            label={user.user_type}
            size="small"
            sx={{ height: 16, fontSize: '0.6rem', mt: 0.25 }}
          />
        )}
      </Box>
    </Box>
  );
};

const RiskSummaryView = ({ data, onUserDoubleClick }) => {
  const { summary, users } = data;
  const stats = [
    { label: 'Dormant', count: summary.dormant_count, color: '#6366f1' },
    { label: 'Priv Creep', count: summary.privilege_creep_count, color: '#d4925e' },
    { label: 'Excessive', count: summary.excessive_access_count, color: '#d27373' },
    { label: 'Critical', count: summary.critical_trust_count, color: '#b85a5a' },
    { label: 'Degraded', count: summary.degraded_trust_count, color: '#bc7a45' },
    { label: 'Contractors', count: summary.contractor_count, color: '#7c3aed' },
  ].filter((s) => s.count > 0);

  return (
    <Box sx={{ mt: 1 }}>
      {stats.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {summary.total_users} users
          </Typography>
          {stats.map((s, i) => (
            <Chip
              key={i}
              label={`${s.count} ${s.label}`}
              size="small"
              sx={{ bgcolor: s.color, color: '#fff', fontWeight: 600, fontSize: '0.7rem' }}
            />
          ))}
        </Box>
      )}
      {users && users.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {users.map((u, i) => (
            <UserMiniCard key={u.email || i} user={u} onDoubleClick={onUserDoubleClick} />
          ))}
        </Box>
      )}
    </Box>
  );
};

const UserListView = ({ data, onUserDoubleClick }) => {
  const { title, users } = data;
  return (
    <Box sx={{ mt: 1 }}>
      {title && (
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          {title}
        </Typography>
      )}
      {users && users.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {users.map((u, i) => (
            <UserMiniCard key={u.email || i} user={u} onDoubleClick={onUserDoubleClick} />
          ))}
        </Box>
      )}
    </Box>
  );
};

const StructuredResponse = ({ data, onUserDoubleClick }) => {
  if (!data) return null;
  if (data.type === 'risk_summary')
    return <RiskSummaryView data={data} onUserDoubleClick={onUserDoubleClick} />;
  if (data.type === 'user_list')
    return <UserListView data={data} onUserDoubleClick={onUserDoubleClick} />;
  return null;
};

/* ─── Sub-components ─── */

const UserDetailCard = ({ userData }) => {
  if (!userData) return null;
  const { user, trust, groups, apps, insights, velocity } = userData;
  const trustColor = getTrustColor(trust?.score);

  return (
    <Box
      sx={{
        mt: 1,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: '0.8rem',
            flexShrink: 0,
          }}
        >
          {getInitials(user?.display_name)}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={700}>
            {user?.display_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {user?.department}
            {user?.job_title ? ` · ${user.job_title}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
        <Box
          sx={{
            border: `2px solid ${trustColor}`,
            borderRadius: 1,
            px: 1,
            py: 0.5,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" fontWeight={700}>
            {trust?.score ?? '—'}
          </Typography>
          <Typography variant="caption" sx={{ color: trustColor, fontSize: '0.65rem' }}>
            {trust?.level || ''}
          </Typography>
        </Box>
      </Box>

      {/* Risk tags */}
      {(insights?.dormant || insights?.privilege_creep || insights?.excessive_access) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
          {insights?.dormant && (
            <Chip
              label={`Dormant (${insights.dormant.days_inactive}d inactive)`}
              size="small"
              sx={{ bgcolor: '#6366f1', color: '#fff', fontSize: '0.7rem' }}
            />
          )}
          {insights?.privilege_creep && (
            <Chip
              label={`Privilege Creep (risk: ${insights.privilege_creep.risk_score})`}
              size="small"
              sx={{ bgcolor: '#d4925e', color: '#fff', fontSize: '0.7rem' }}
            />
          )}
          {insights?.excessive_access && (
            <Chip
              label={`Excessive Access${typeof insights.excessive_access.z_score === 'number' ? ` (z-score: ${insights.excessive_access.z_score.toFixed(1)})` : ''}`}
              size="small"
              sx={{ bgcolor: '#d27373', color: '#fff', fontSize: '0.7rem' }}
            />
          )}
        </Box>
      )}

      {/* Access stats */}
      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {groups?.length || 0} groups
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {apps?.length || 0} apps
        </Typography>
        {velocity && (velocity.groups_added_7d > 0 || velocity.apps_added_7d > 0) && (
          <Typography variant="caption" color="warning.main">
            +{(velocity.groups_added_7d || 0) + (velocity.apps_added_7d || 0)} changes/7d
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const decisionColors = {
  approve: { bg: 'rgba(93, 168, 122, 0.12)', border: '#5da87a', color: '#356b4a' },
  flag: { bg: '#fef9c3', border: '#eab308', color: '#854d0e' },
  revoke: { bg: 'rgba(210, 115, 115, 0.12)', border: '#d27373', color: '#8a3d3d' },
};

const MessageBubble = React.memo(
  ({ message, onAction, onChipClick, reviewDecisions, onUserDoubleClick }) => {
    const isUser = message.role === 'user';
    const isSystem = message.role === 'system';

    const actionUserEmail = message.actions?.[0]?.userEmail;
    const currentDecision = actionUserEmail ? reviewDecisions[actionUserEmail] : null;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: isUser ? 'row-reverse' : 'row',
          gap: 1,
          mb: 1.5,
          alignItems: 'flex-start',
        }}
      >
        {/* AI avatar */}
        {!isUser && !isSystem && (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              mt: 0.5,
            }}
          >
            <Shield size={14} />
          </Box>
        )}

        {/* Bubble */}
        <Box
          sx={{
            maxWidth: isSystem ? '100%' : '85%',
            width: isSystem ? '100%' : undefined,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: isUser ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
              bgcolor: isUser ? 'primary.main' : isSystem ? 'action.selected' : 'background.paper',
              color: isUser ? 'primary.contrastText' : 'text.primary',
              border: isUser ? 'none' : '1px solid',
              borderColor: 'divider',
              '& p': { m: 0 },
              '& p + p': { mt: 0.5 },
              '& ul, & ol': { pl: 2, m: 0 },
              '& li': { mb: 0.25 },
              '& strong': { fontWeight: 700 },
              '& code': {
                bgcolor: 'action.hover',
                px: 0.5,
                borderRadius: 0.5,
                fontFamily: 'monospace',
                fontSize: '0.85em',
              },
            }}
          >
            {isSystem ? (
              <Typography variant="body2">{message.content}</Typography>
            ) : (
              <Typography variant="body2" component="div">
                <ReactMarkdown>{message.content}</ReactMarkdown>
              </Typography>
            )}
          </Box>

          {message.structuredData && (
            <StructuredResponse data={message.structuredData} onUserDoubleClick={onUserDoubleClick} />
          )}

          {message.userData && <UserDetailCard userData={message.userData} />}

          {/* Action buttons */}
          {message.actions && !currentDecision && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              {message.actions.map((action) => (
                <Button
                  key={action.action}
                  size="small"
                  variant="outlined"
                  onClick={() => onAction(action)}
                  startIcon={
                    action.action === 'approve' ? (
                      <CheckCircle2 size={14} />
                    ) : action.action === 'flag' ? (
                      <Flag size={14} />
                    ) : (
                      <Ban size={14} />
                    )
                  }
                  sx={{
                    borderColor:
                      action.variant === 'success'
                        ? '#5da87a'
                        : action.variant === 'warning'
                          ? '#eab308'
                          : '#d27373',
                    color:
                      action.variant === 'success'
                        ? '#4a9066'
                        : action.variant === 'warning'
                          ? '#a16207'
                          : '#b85a5a',
                    '&:hover': {
                      bgcolor:
                        action.variant === 'success'
                          ? 'rgba(93, 168, 122, 0.12)'
                          : action.variant === 'warning'
                            ? '#fef9c3'
                            : 'rgba(210, 115, 115, 0.12)',
                    },
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          )}

          {/* Decision badge */}
          {message.actions && currentDecision && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 1,
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                border: `1px solid ${decisionColors[currentDecision.decision]?.border || '#6b7280'}`,
                bgcolor: decisionColors[currentDecision.decision]?.bg || '#f9fafb',
                color: decisionColors[currentDecision.decision]?.color || '#374151',
              }}
            >
              {currentDecision.decision === 'approve' && <CheckCircle2 size={14} />}
              {currentDecision.decision === 'flag' && <Flag size={14} />}
              {currentDecision.decision === 'revoke' && <Ban size={14} />}
              <Typography variant="caption" fontWeight={600}>
                {currentDecision.decision.charAt(0).toUpperCase() +
                  currentDecision.decision.slice(1)}
                d
              </Typography>
            </Box>
          )}

          {/* Suggestions */}
          {message.suggestions && message.suggestions.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
              {message.suggestions.map((chip, i) => (
                <Chip
                  key={i}
                  label={chip}
                  size="small"
                  variant="outlined"
                  onClick={() => onChipClick(chip)}
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                />
              ))}
            </Box>
          )}

          {/* Timestamp */}
          {message.timestamp && (
            <Typography
              variant="caption"
              color="text.disabled"
              display="block"
              sx={{ mt: 0.5, textAlign: isUser ? 'right' : 'left' }}
            >
              {new Date(message.timestamp).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Typography>
          )}
        </Box>
      </Box>
    );
  },
);

/* ─── Main Component ─── */

const AccessReviewPage = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const tenantName = user?.['https://trustx.cloud/claims/company'];

  const [isInitialized, setIsInitialized] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);

  const [reviewPhase, setReviewPhase] = useState('greeting');
  const [reviewData, setReviewData] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  const [reviewDecisions, setReviewDecisions] = useState({});
  const [focusedUserEmail, setFocusedUserEmail] = useState(null);

  const [popupUser, setPopupUser] = useState(null);

  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  const [showProgress, setShowProgress] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [pastSessions, setPastSessions] = useState([]);

  const messagesEndRef = useRef(null);

  const users = useMemo(() => reviewData?.users || [], [reviewData]);

  const totalUsers = users.length;
  const reviewedCount = Object.keys(reviewDecisions).length;
  const progressPct = totalUsers > 0 ? Math.round((reviewedCount / totalUsers) * 100) : 0;
  const approvedCount = Object.values(reviewDecisions).filter((d) => d.decision === 'approve').length;
  const flaggedCount = Object.values(reviewDecisions).filter((d) => d.decision === 'flag').length;
  const revokedCount = Object.values(reviewDecisions).filter((d) => d.decision === 'revoke').length;

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, chatLoading]);

  const addMessage = useCallback((msg) => {
    setMessages((prev) => [
      ...prev,
      { id: nextMsgId(), timestamp: new Date(), ...msg },
    ]);
  }, []);

  // Initialize
  useEffect(() => {
    const init = async () => {
      if (!tenantName) {
        setError('Unable to determine your organization.');
        return;
      }
      try {
        const token = await getAccessTokenSilently();
        graphragService.setAuthToken(token);
        const sid = graphragService.generateSessionId();
        setSessionId(sid);
        setIsInitialized(true);

        setMessages([
          {
            id: nextMsgId(),
            role: 'assistant',
            content:
              "Welcome! I'm your **Access Review Assistant**. I'll help you evaluate user access, identify risks, and make informed decisions through conversation.\n\nWhat scope would you like to review?",
            timestamp: new Date(),
            suggestions: [
              'Review all users',
              'Review dormant accounts',
              'Review privilege creep',
              'Review excessive access',
            ],
          },
        ]);
      } catch (err) {
        console.error('Failed to initialize:', err);
        setError('Failed to initialize. Please refresh the page.');
      }
    };
    init();
  }, [getAccessTokenSilently, tenantName]);

  const findNextUser = useCallback(
    (excludeEmail) => {
      return (
        users
          .filter((u) => u.email !== excludeEmail && !reviewDecisions[u.email])
          .sort((a, b) => (a.trust_score ?? 100) - (b.trust_score ?? 100))[0] || null
      );
    },
    [users, reviewDecisions],
  );

  const generateSuggestions = useCallback(() => {
    const suggestions = [];
    const remaining = totalUsers - reviewedCount;

    if (remaining > 0) {
      const next = findNextUser(null);
      if (next) suggestions.push(`Tell me about ${next.display_name}`);
      suggestions.push('Show me the riskiest remaining users');
    }
    if (remaining > 0 && reviewedCount > 0) {
      suggestions.push(`${remaining} users left to review`);
    }
    if (reviewedCount > 0) {
      suggestions.push('Show review progress');
    }

    return suggestions.slice(0, 4);
  }, [totalUsers, reviewedCount, findNextUser]);

  const loadPastSessions = useCallback(async () => {
    try {
      const result = await graphragService.getReviewSessions();
      if (result.success && result.data?.sessions) {
        setPastSessions(result.data.sessions);
      }
    } catch (err) {
      console.error('Failed to load past sessions:', err);
    }
  }, []);

  useEffect(() => {
    if (isInitialized) loadPastSessions();
  }, [isInitialized, loadPastSessions]);

  const handleLoadSession = useCallback(async (session) => {
    try {
      setChatLoading(true);
      const result = await graphragService.getReviewSessionMessages(session.session_id);
      if (result.success && result.data?.messages) {
        const loaded = result.data.messages.map((m) => ({
          id: nextMsgId(),
          role: m.role,
          content: m.content,
          timestamp: new Date(m.created_at),
        }));
        setMessages(loaded);
        setSessionId(session.session_id);
        setReviewId(session.review_id);
        setReviewPhase('active');
        setShowHistory(false);
      }
    } catch (err) {
      console.error('Failed to load session:', err);
    } finally {
      setChatLoading(false);
    }
  }, []);

  const handleScopeSelection = useCallback(
    async (text) => {
      addMessage({ role: 'user', content: text });
      setChatLoading(true);
      const { scope, scopeValue } = parseScopeIntent(text);
      try {
        const response = await graphragService.startReview(scope, scopeValue, sessionId);
        if (response.success && response.data) {
          const data = response.data;
          setReviewData(data);
          setReviewId(data.review_id);
          setReviewPhase('active');
          loadPastSessions();
          addMessage({
            role: 'assistant',
            content: buildReviewSummary(data),
            suggestions: data.suggested_queries || [
              'Show me the riskiest users',
              'Who has the lowest trust score?',
              'Summarize by department',
            ],
          });
        } else {
          throw new Error(response.error || 'Failed to start review');
        }
      } catch (err) {
        console.error('Failed to start review:', err);
        setError(err.message || 'Failed to start access review');
      } finally {
        setChatLoading(false);
      }
    },
    [addMessage, sessionId, loadPastSessions],
  );

  const handleDecision = useCallback(
    async (userEmail, decision, userName) => {
      if (!reviewId) return;
      setChatLoading(true);
      try {
        const response = await graphragService.submitReviewDecision(
          reviewId,
          userEmail,
          decision,
          null,
        );
        if (response.success) {
          setReviewDecisions((prev) => ({
            ...prev,
            [userEmail]: { decision, timestamp: new Date().toISOString() },
          }));
          const label = { approve: 'Approved', flag: 'Flagged', revoke: 'Revoked' }[decision];
          const nextUser = findNextUser(userEmail);
          let content = `**${label}**: ${userName || userEmail}\n\nDecision recorded successfully.`;
          if (nextUser) {
            content += `\n\nNext suggested: **${nextUser.display_name}** (Trust Score: ${nextUser.trust_score ?? '—'}, ${nextUser.trust_level || 'UNKNOWN'})`;
          } else {
            const remaining = totalUsers - reviewedCount - 1;
            if (remaining <= 0) {
              content +=
                '\n\nAll users have been reviewed! You can view the summary or start a new review.';
            }
          }
          addMessage({
            role: 'assistant',
            content,
            suggestions: nextUser
              ? [
                  `Tell me about ${nextUser.display_name}`,
                  'Show riskiest remaining users',
                  'Show review progress',
                ]
              : ['Show review summary', 'Start new review'],
          });
        }
      } catch (err) {
        console.error('Failed to submit decision:', err);
        setError('Failed to record decision. Please try again.');
      } finally {
        setChatLoading(false);
      }
    },
    [reviewId, addMessage, findNextUser, totalUsers, reviewedCount],
  );

  const handleUserDoubleClick = useCallback((user) => {
    if (!user?.email) return;
    setPopupUser({
      email: user.email,
      displayName: user.display_name,
      name: user.display_name,
      type: 'User',
      department: user.department,
      jobTitle: user.job_title,
    });
  }, []);

  const handlePopupClose = useCallback(() => setPopupUser(null), []);

  const handlePopupReviewDecision = useCallback(
    async (userEmail, decision) => {
      if (!userEmail) return;
      await handleDecision(userEmail, decision, popupUser?.displayName);
      setPopupUser(null);
    },
    [popupUser, handleDecision],
  );

  const handleUserDetail = useCallback(
    async (matchedUser) => {
      setFocusedUserEmail(matchedUser.email);
      setChatLoading(true);
      try {
        const results = await Promise.allSettled([
          graphragService.getReviewUserDetail(reviewId, matchedUser.email),
          graphragService.reviewChat(
            reviewId,
            `Analyze ${matchedUser.display_name} (${matchedUser.email}) for access review. Summarize their risk profile and recommend an action.`,
            sessionId,
            matchedUser.email,
          ),
        ]);

        const detailResult = results[0];
        const chatResult = results[1];

        const userData =
          detailResult.status === 'fulfilled' && detailResult.value.success
            ? detailResult.value.data
            : null;
        const chatData =
          chatResult.status === 'fulfilled' && chatResult.value.success
            ? chatResult.value.data
            : null;
        const aiText =
          chatData?.response ||
          `Here's what I found for **${matchedUser.display_name}** (${matchedUser.email}).`;

        addMessage({
          role: 'assistant',
          content: aiText,
          userData,
          actions: !reviewDecisions[matchedUser.email]
            ? [
                {
                  label: 'Approve',
                  action: 'approve',
                  userEmail: matchedUser.email,
                  userName: matchedUser.display_name,
                  variant: 'success',
                },
                {
                  label: 'Flag for Review',
                  action: 'flag',
                  userEmail: matchedUser.email,
                  userName: matchedUser.display_name,
                  variant: 'warning',
                },
                {
                  label: 'Revoke Access',
                  action: 'revoke',
                  userEmail: matchedUser.email,
                  userName: matchedUser.display_name,
                  variant: 'danger',
                },
              ]
            : null,
          suggestions:
            chatData?.suggestions?.length > 0
              ? chatData.suggestions
              : [
                  `What groups does ${matchedUser.display_name} have?`,
                  'Show next riskiest user',
                  'Show review progress',
                ],
        });
      } catch (err) {
        console.error('User detail error:', err);
        addMessage({
          role: 'assistant',
          content: 'I encountered an error fetching user details. Please try again.',
        });
      } finally {
        setChatLoading(false);
      }
    },
    [reviewId, sessionId, addMessage, reviewDecisions],
  );

  const handleActionClick = useCallback(
    (action) => {
      handleDecision(action.userEmail, action.action, action.userName);
    },
    [handleDecision],
  );

  const processMessage = useCallback(
    async (message) => {
      if (reviewPhase === 'greeting') return handleScopeSelection(message);

      addMessage({ role: 'user', content: message });
      setChatLoading(true);

      try {
        const decisionIntent = parseDecisionIntent(message, users);
        if (decisionIntent) {
          await handleDecision(
            decisionIntent.userEmail,
            decisionIntent.decision,
            decisionIntent.userName,
          );
          return;
        }

        const userDetailIntent = parseUserDetailIntent(message, users);
        if (userDetailIntent) {
          await handleUserDetail(userDetailIntent);
          return;
        }

        const response = await graphragService.reviewChat(
          reviewId,
          message,
          sessionId,
          focusedUserEmail,
        );

        if (response.success && response.data) {
          addMessage({
            role: 'assistant',
            content: response.data.response,
            structuredData: response.data.structured_data || null,
            suggestions:
              response.data.suggestions?.length > 0
                ? response.data.suggestions
                : generateSuggestions(),
          });
        } else {
          addMessage({
            role: 'assistant',
            content: 'I encountered an error processing your question. Please try again.',
          });
        }
      } catch (err) {
        console.error('Chat error:', err);
        addMessage({
          role: 'assistant',
          content: 'I encountered an error processing your question. Please try again.',
        });
      } finally {
        setChatLoading(false);
      }
    },
    [
      reviewPhase,
      handleScopeSelection,
      addMessage,
      users,
      handleDecision,
      handleUserDetail,
      reviewId,
      sessionId,
      focusedUserEmail,
      generateSuggestions,
    ],
  );

  const handleSendChat = useCallback(() => {
    if (!chatInput.trim() || chatLoading) return;
    const message = chatInput.trim();
    setChatInput('');
    processMessage(message);
  }, [chatInput, chatLoading, processMessage]);

  const handleChipClick = useCallback(
    (chip) => {
      setChatInput('');
      processMessage(chip);
    },
    [processMessage],
  );

  const handleNewReview = useCallback(() => {
    msgCounter = 0;
    setReviewPhase('greeting');
    setReviewData(null);
    setReviewId(null);
    setReviewDecisions({});
    setFocusedUserEmail(null);
    setShowProgress(false);
    setError(null);
    setSessionId(graphragService.generateSessionId());
    setMessages([
      {
        id: nextMsgId(),
        role: 'assistant',
        content:
          "Welcome! I'm your **Access Review Assistant**. I'll help you evaluate user access, identify risks, and make informed decisions through conversation.\n\nWhat scope would you like to review?",
        timestamp: new Date(),
        suggestions: [
          'Review all users',
          'Review dormant accounts',
          'Review privilege creep',
          'Review excessive access',
        ],
      },
    ]);
  }, []);

  // Loading state
  if (!isInitialized) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Initializing Access Review...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShieldIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" fontWeight={700}>
            Access Review — AI Assistant
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Clock size={14} />}
            onClick={() => {
              setShowHistory((prev) => !prev);
              setShowProgress(false);
            }}
          >
            History
          </Button>
          {reviewPhase === 'active' && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<BarChart3 size={14} />}
              onClick={() => {
                setShowProgress((prev) => !prev);
                setShowHistory(false);
              }}
            >
              {reviewedCount}/{totalUsers} reviewed
            </Button>
          )}
          {reviewPhase === 'active' && (
            <Button
              size="small"
              variant="contained"
              startIcon={<RefreshCw size={14} />}
              onClick={handleNewReview}
            >
              New Review
            </Button>
          )}
        </Box>
      </Box>

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ borderRadius: 0 }}
        >
          {error}
        </Alert>
      )}

      {/* Chat container */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Input area — top */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              placeholder={
                reviewPhase === 'greeting'
                  ? 'Type a scope or click a suggestion below...'
                  : 'Ask about users, risks, or type "approve [name]"...'
              }
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendChat();
                }
              }}
              disabled={chatLoading}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <IconButton
              color="primary"
              onClick={handleSendChat}
              disabled={chatLoading || !chatInput.trim()}
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
              }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            py: 2,
          }}
        >
          {messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              onAction={handleActionClick}
              onChipClick={handleChipClick}
              reviewDecisions={reviewDecisions}
              onUserDoubleClick={handleUserDoubleClick}
            />
          ))}

          {chatLoading && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  mt: 0.5,
                }}
              >
                <Shield size={14} />
              </Box>
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: '4px 16px 16px 16px',
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {[0, 1, 2].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        animation: 'bounce 1.4s infinite',
                        animationDelay: `${i * 0.2}s`,
                        '@keyframes bounce': {
                          '0%, 60%, 100%': { transform: 'translateY(0)' },
                          '30%': { transform: 'translateY(-6px)' },
                        },
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Analyzing...
                </Typography>
              </Box>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>
      </Box>

      {/* Progress Sidebar */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 280,
          bgcolor: 'background.paper',
          borderLeft: '1px solid',
          borderColor: 'divider',
          transform: showProgress ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.25s ease',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 10,
          p: 2,
          gap: 2,
          overflowY: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Review Progress
          </Typography>
          <IconButton size="small" onClick={() => setShowProgress(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box>
          <LinearProgress variant="determinate" value={progressPct} sx={{ borderRadius: 2, mb: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {reviewedCount} of {totalUsers} reviewed ({progressPct}%)
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {approvedCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle2 size={14} color="#5da87a" />
              <Typography variant="body2">{approvedCount} Approved</Typography>
            </Box>
          )}
          {flaggedCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Flag size={14} color="#eab308" />
              <Typography variant="body2">{flaggedCount} Flagged</Typography>
            </Box>
          )}
          {revokedCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Ban size={14} color="#d27373" />
              <Typography variant="body2">{revokedCount} Revoked</Typography>
            </Box>
          )}
        </Box>

        {reviewData?.risk_summary && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {reviewData.risk_summary.critical > 0 && (
              <Chip label={`${reviewData.risk_summary.critical} Critical`} size="small" sx={{ bgcolor: '#b85a5a', color: '#fff' }} />
            )}
            {reviewData.risk_summary.dormant > 0 && (
              <Chip label={`${reviewData.risk_summary.dormant} Dormant`} size="small" sx={{ bgcolor: '#6366f1', color: '#fff' }} />
            )}
            {reviewData.risk_summary.privilege_creep > 0 && (
              <Chip label={`${reviewData.risk_summary.privilege_creep} Privilege Creep`} size="small" sx={{ bgcolor: '#d4925e', color: '#fff' }} />
            )}
            {reviewData.risk_summary.excessive_access > 0 && (
              <Chip label={`${reviewData.risk_summary.excessive_access} Excessive`} size="small" sx={{ bgcolor: '#d27373', color: '#fff' }} />
            )}
          </Box>
        )}

        <Button variant="outlined" color="error" onClick={handleNewReview} fullWidth>
          End Review & Start New
        </Button>
      </Box>

      {/* History Sidebar */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 300,
          bgcolor: 'background.paper',
          borderLeft: '1px solid',
          borderColor: 'divider',
          transform: showHistory ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.25s ease',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 10,
          overflowY: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Past Reviews
          </Typography>
          <IconButton size="small" onClick={() => setShowHistory(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {pastSessions.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              color: 'text.secondary',
            }}
          >
            <MessageSquare size={24} />
            <Typography variant="body2">No past reviews yet</Typography>
          </Box>
        ) : (
          <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {pastSessions.map((s) => (
              <Box
                key={s.session_id}
                component="button"
                onClick={() => handleLoadSession(s)}
                sx={{
                  width: '100%',
                  textAlign: 'left',
                  p: 1.5,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: s.session_id === sessionId ? 'primary.main' : 'divider',
                  bgcolor:
                    s.session_id === sessionId ? 'primary.50' : 'background.paper',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Typography variant="body2" fontWeight={600} noWrap>
                  {s.title || `${s.scope || 'all'} review`}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(s.updated_at || s.started_at).toLocaleDateString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.total_messages || 0} msgs
                  </Typography>
                  {s.decisions_made > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {s.decisions_made} decisions
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* User insights popup */}
      {popupUser && (
        <UserDetailPopup
          user={popupUser}
          onClose={handlePopupClose}
          graphragService={graphragService}
          tenantId={tenantName}
          reviewMode={true}
          reviewDecision={reviewDecisions[popupUser.email] || null}
          onReviewDecision={handlePopupReviewDecision}
        />
      )}
    </Box>
  );
};

export default AccessReviewPage;
